import React, { useContext } from 'react';

import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';

import ChallengesByTypeCard from './components/ChallengesByTypeCard/ChallengesByTypeCard';
import ChallengesRankingCard from './components/ChallengesRankingCard/ChallengesRankingCard';
import ChallengesSummaryCard from './components/ChallengesSummaryCard/ChallengesSummaryCard';
import EmptyCard from '../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import { CREATE_CHALLENGE_ROUTE } from '../../../../shared/constants/auth';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { MetricsContext } from '../../../../shared/context/metrics/metricsContext';
import { useLatestRequest } from '../../../../shared/hooks/useLatestRequest/useLatestRequest';
import {
  IMetricsChallengeByType,
  IMetricsChallengeRanking,
  IMetricsChallengesSummary
} from '../../../../shared/interfaces/metrics';
import MetricsByCompany from '../../../../shared/services/api/metrics/metricsByCompany/metrics';
import { TRANSLATIONS_METRICS_NE } from '../../../../shared/translations/metrics';
import { getCurrentDateYYYYMMDD } from '../../../../shared/utils/datetime/datetime';
import { redirectToPage } from '../../../../shared/utils/utils';
import { isEmptyObject } from '../../../../shared/utils/validation/validation';

import './ChallengesMetrics.scss';

const ChallengesMetrics = () => {
  const { company } = useContext(CompanyContext);
  const { periodSelection } = useContext(MetricsContext);

  const navigate = useNavigate();

  const summary = useLatestRequest<IMetricsChallengesSummary | undefined>(
    () => MetricsByCompany.getChallengesSummaryMetrics(company.id, periodSelection.period, periodSelection.month),
    [company, periodSelection],
    undefined
  );
  const byType = useLatestRequest<IMetricsChallengeByType[]>(
    () => MetricsByCompany.getChallengesByTypeMetrics(company.id, periodSelection.period, periodSelection.month),
    [company, periodSelection],
    []
  );
  const ranking = useLatestRequest<IMetricsChallengeRanking[]>(
    () => MetricsByCompany.getChallengesRankingMetrics(company.id, periodSelection.period, periodSelection.month),
    [company, periodSelection],
    []
  );

  const createChallenge = () => {
    redirectToPage(navigate, CREATE_CHALLENGE_ROUTE);
  };

  const hasSummaryData = summary.data !== undefined && !isEmptyObject(summary.data);
  const isSummaryEmpty = !summary.loading && !summary.error && !hasSummaryData;
  const isByTypeEmpty = !byType.loading && !byType.error && byType.data.length === 0;
  const isRankingEmpty = !ranking.loading && !ranking.error && ranking.data.every((item) => !item.entries?.length);
  const isEmpty = isSummaryEmpty && isByTypeEmpty && isRankingEmpty;

  if (isEmpty) {
    const isCurrentMonth =
      !periodSelection.month || periodSelection.month.slice(0, 7) === getCurrentDateYYYYMMDD().slice(0, 7);

    if (!isCurrentMonth) {
      return (
        <EmptyCard
          center
          className="challenges-metrics-empty"
          primaryText={t(TRANSLATIONS_METRICS_NE.challenges.emptyPastMonth.title)}
          secondaryText={t(TRANSLATIONS_METRICS_NE.challenges.emptyPastMonth.description)}
        />
      );
    }

    return (
      <EmptyCard
        center
        className="challenges-metrics-empty"
        primaryText={t(TRANSLATIONS_METRICS_NE.challenges.emptyPage.title)}
        secondaryText={t(TRANSLATIONS_METRICS_NE.challenges.emptyPage.description)}
        buttonText={t(TRANSLATIONS_METRICS_NE.challenges.emptyPage.button)}
        action={createChallenge}
      />
    );
  }

  return (
    <div className="flex flex-column gap-4">
      <ChallengesSummaryCard
        data={summary.data}
        loading={summary.loading}
        error={summary.error}
        onRetry={summary.retry}
      />
      <ChallengesByTypeCard data={byType.data} loading={byType.loading} error={byType.error} onRetry={byType.retry} />
      <ChallengesRankingCard
        data={ranking.data}
        loading={ranking.loading}
        error={ranking.error}
        onRetry={ranking.retry}
      />
    </div>
  );
};

export default ChallengesMetrics;
